import { useEffect, useRef, type ComponentType } from 'react';
import { Box } from '@radix-ui/themes';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingFlow } from '@/hooks/useOnboardingFlow';
import { useUserProfile } from '@/hooks/useUserProfile';
import { analyticsClient } from '@/lib';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { AboutStep } from './AboutStep';
import { CityStep } from './CityStep';
import { EthnicityStep } from './EthnicityStep';
import { HeightStep } from './HeightStep';
import { IdentityStep } from './IdentityStep';
import { InterestsStep } from './InterestsStep';
import { IntimacyStep } from './IntimacyStep';
import { LanguagesStep } from './LanguagesStep';
import { LocationStep } from './LocationStep';
import { NameStep } from './NameStep';
import { NotificationsStep } from './NotificationsStep';
import { PersonalityIntroStep } from './PersonalityIntroStep';
import { PhotosStep } from './PhotosStep';
import { SetIntentionsStep } from './SetIntentionsStep';
import { SituationStep } from './SituationStep';
import { SpotsStep } from './SpotsStep';
import type { OnboardingStepProps } from './types';

interface OnboardingFlowProps {
  onComplete: () => void;
}

const ONBOARDING_STEPS: ComponentType<OnboardingStepProps>[] = [
  PersonalityIntroStep,
  NameStep,
  PhotosStep,
  IdentityStep,
  CityStep,
  LanguagesStep,
  EthnicityStep,
  HeightStep,
  SituationStep,
  InterestsStep,
  SpotsStep,
  IntimacyStep,
  AboutStep,
  LocationStep,
  NotificationsStep,
  SetIntentionsStep,
];

/** Onboarding flow router — picks the current step and passes shared flow state. */
export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const { user } = useAuth();
  const { profile } = useUserProfile(user);
  const setUserContext = useOnboardingStore(state => state.setUserContext);
  const fetchUserData = useOnboardingStore(state => state.fetchUserData);
  const flow = useOnboardingFlow(onComplete);
  const { setStep } = flow;
  const Step = ONBOARDING_STEPS[flow.step];
  const hasResumed = useRef(false);

  useEffect(() => {
    if (!user?.uid || hasResumed.current) return;

    const { uid: storeUid, userName } = useOnboardingStore.getState();
    setUserContext(
      user.uid,
      storeUid === user.uid && userName.trim() ? userName : (profile?.name ?? ''),
      user.email ?? ''
    );

    void (async () => {
      await fetchUserData();
      if (!hasResumed.current) {
        hasResumed.current = true;
        const resumedStep = useOnboardingStore.getState().onboardingStep;
        analyticsClient.capture('onboarding_started', { resumed_step: resumedStep });
        setStep(resumedStep);
      }
    })();
  }, [user?.uid, user?.email, profile?.name, setUserContext, fetchUserData, setStep]);

  if (!Step) {
    return null;
  }

  return (
    <Box flexGrow="1" minHeight="0" height="100%" width="100%">
      <Step flow={flow} />
    </Box>
  );
}
